/**
 * Copyright © 2026 OwnersTable Inc. All rights reserved.
 * This source code is proprietary and confidential.
 * Unauthorized copying or distribution is strictly prohibited.
 */

import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Breadcrumbs,
  FormControl,
  IconButton,
  InputAdornment,
  Select,
} from "@mui/material";
import ClearIcon from "@mui/icons-material/Clear";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import dayjs from "dayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { keepPreviousData, useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { NavigableBreadcrumbLink } from "../../../components/page_breadcrumbs/navigable_breadcrumb_link";
import api from "../../../api";
import DropdownSearchBar from "../../../components/dorpdown_searchbar";
import { DropdownInfiniteLoader } from "../../../components/dropdown_infinite_loader";
import { IndexTable } from "../../../components/index_table";
import { Loader } from "../../../components/loader";
import { MenuHeading } from "../../../components/menu_heading";
import { ResetButton } from "../../../components/reset_button";
import { SelectedMenuItem } from "../../../components/selected_menu_item";
import { StyledMenuItem } from "../../../components/styled_menu_item";
import { UpdateButton } from "../../../components/update_button";
import { Query_Keys } from "../../../lib/queryKeys";
import { useNotificationContext } from "../../../contexts/notificationContext";
import { PerPage } from "../../../constants/perPage";
import { useDebounce } from "../../../utils/useDebounce";
import {
  getDropdownNextPageParam,
  mergeOptionsIfChanged,
  mergeUniqueByKey,
  normalizeRestaurant,
  normalizeRestaurantGroup,
} from "../../../utils/dropdownInfiniteQuery";
import { useInfiniteScrollObserver } from "../../../hooks/useInfiniteScrollObserver";
import { ALL_FILTER_VALUE } from "../../../utils/perqReportDrilldown";
import {
  StyledBreadCrumbContainer,
  StyledLink,
  filterContainerStyles,
  selectBaseStyles,
  datePickerStyles,
  dropdownMenuProps,
  groupSpacerStyles,
  groupBandStyles,
  columnHeaderStyles,
  totalRowStyles,
  detailRowStyles,
  negativeCellStyles,
  groupTotalRowStyles,
  groupTotalLabelStyles,
} from "./styles";
import { LightTooltip, StyledInfoIcon } from "../../../components/statistics_cards/styles";
import { scopeToParams } from "../../../components/restaurant_scope_filters";
import { LaborReportExportDialog } from "./export_dialog";
import { buildGroupExportUrl, exportFilename } from "./export";
import { useLaborReportExport } from "./use_labor_report_export";

// Client-side twin of the endpoint's only validation error, caught before the request goes out.
const INVALID_DATE_MESSAGE = "Business date must be a valid ISO date (yyyy-mm-dd)";

const GROUP_TOTAL_TOOLTIP =
  "Total labor cost divided by total sales for all restaurants in the filters — " +
  "not the average of the rows above.";

// The four measure columns repeat identically under Daily and MTD.
const MEASURES = [
  { field: "current", labelFrom: "current" },
  { field: "prior", labelFrom: "prior" },
  { field: "amt_var", label: "Amt Var" },
  { field: "pct_var", label: "% Var" },
];

const PERIODS = ["daily", "mtd"];

const defaultLaborFilters = () => ({
  restaurant_group_id: ALL_FILTER_VALUE,
  restaurant_sub_group_id: ALL_FILTER_VALUE,
  restaurant_ids: [ALL_FILTER_VALUE],
  business_date: dayjs(),
});

const normalizeDayjsValue = (value) => {
  if (!value) return null;
  // A half-typed date arrives as a dayjs object that is truthy and formats to the literal
  // string "Invalid Date". Collapse it to null so the apply guard catches it locally
  // instead of sending business_date=Invalid Date and taking a 422 to find out.
  if (typeof value?.isValid === "function" && typeof value?.format === "function") {
    return value.isValid() ? value : null;
  }
  const parsed = dayjs(value?.$d || value);
  return parsed.isValid() ? parsed : null;
};

// The API is ISO-only on input even though the report prints mm/dd/yyyy.
const toIsoDate = (value) => {
  const parsed = normalizeDayjsValue(value);
  return parsed ? parsed.format("YYYY-MM-DD") : undefined;
};

const measureCells = (row) =>
  PERIODS.reduce((acc, period) => {
    MEASURES.forEach(({ field }) => {
      const cell = row?.[period]?.[field];
      acc[`${period}_${field}`] = cell?.negative ? (
        <Box component="span" data-negative="true" sx={negativeCellStyles}>
          {cell.display}
        </Box>
      ) : (
        cell?.display ?? "--"
      );
    });
    return acc;
  }, {});

export const LaborReport = () => {
  const showNotification = useNotificationContext();

  const [filters, setFilters] = useState(defaultLaborFilters());
  const [appliedFilters, setAppliedFilters] = useState(defaultLaborFilters());
  const [filtersChanged, setFiltersChanged] = useState(false);

  const [restaurantGroupSearch, setRestaurantGroupSearch] = useState("");
  const [restaurantSubGroupSearch, setRestaurantSubGroupSearch] = useState("");
  const [restaurantSearch, setRestaurantSearch] = useState("");
  const [restaurantGroupOptionsCache, setRestaurantGroupOptionsCache] = useState([]);
  const [restaurantSubGroupOptionsCache, setRestaurantSubGroupOptionsCache] = useState([]);
  const [restaurantOptionsCache, setRestaurantOptionsCache] = useState([]);

  const [itemsPerPage, setItemsPerPage] = useState(25);
  const [page, setPage] = useState(1);
  const [exportOpen, setExportOpen] = useState(false);

  const { exporting, runExport } = useLaborReportExport();

  const debouncedRestaurantGroupSearch = useDebounce(restaurantGroupSearch.trim(), 300);
  const debouncedRestaurantSubGroupSearch = useDebounce(restaurantSubGroupSearch.trim(), 300);
  const debouncedRestaurantSearch = useDebounce(restaurantSearch.trim(), 300);

  const hasGroupSelected = filters.restaurant_group_id !== ALL_FILTER_VALUE;
  const hasSubGroupSelected = filters.restaurant_sub_group_id !== ALL_FILTER_VALUE;

  const fetchRestaurantGroups = async ({ pageParam = 1 }) => {
    const response = await api.get("/v1/admin/dropdowns/restaurant_groups_list", {
      params: {
        page: pageParam,
        per_page: PerPage,
        search: debouncedRestaurantGroupSearch || undefined,
      },
    });
    return response?.data?.map(normalizeRestaurantGroup) || [];
  };

  const {
    data: restaurantGroups = [],
    isFetching: restaurantGroupFetching,
    isFetchingNextPage: restaurantGroupsFetchingNextPage,
    fetchNextPage: fetchNextRestaurantGroupsPage,
    hasNextPage: hasNextRestaurantGroupsPage,
  } = useInfiniteQuery({
    queryKey: Query_Keys.restaurant_groups_dropdown({
      search: debouncedRestaurantGroupSearch,
      source: "labor_report",
    }),
    queryFn: fetchRestaurantGroups,
    initialPageParam: 1,
    getNextPageParam: getDropdownNextPageParam,
    select: (data) => data.pages.flat(),
    staleTime: 10 * 60 * 1000,
    placeholderData: keepPreviousData,
  });

  const { rootRef: restaurantGroupMenuRef, targetRef: restaurantGroupLoaderRef } =
    useInfiniteScrollObserver({
      fetchNextPage: fetchNextRestaurantGroupsPage,
      hasNextPage: hasNextRestaurantGroupsPage,
      isFetchingNextPage: restaurantGroupsFetchingNextPage,
    });

  const fetchRestaurantSubGroups = async ({ pageParam = 1 }) => {
    if (!hasGroupSelected) return [];
    const response = await api.get("/v1/admin/dropdowns/restaurant_sub_groups_list", {
      params: {
        restaurant_group_id: filters.restaurant_group_id,
        page: pageParam,
        per_page: PerPage,
        search: debouncedRestaurantSubGroupSearch || undefined,
      },
    });
    return response?.data || [];
  };

  const {
    data: restaurantSubGroups = [],
    isFetching: restaurantSubGroupFetching,
    isFetchingNextPage: restaurantSubGroupsFetchingNextPage,
    fetchNextPage: fetchNextRestaurantSubGroupsPage,
    hasNextPage: hasNextRestaurantSubGroupsPage,
  } = useInfiniteQuery({
    queryKey: Query_Keys.restaurant_sub_groups_dropdown({
      restaurant_group_id: filters.restaurant_group_id,
      search: debouncedRestaurantSubGroupSearch,
      source: "labor_report",
    }),
    queryFn: fetchRestaurantSubGroups,
    initialPageParam: 1,
    getNextPageParam: getDropdownNextPageParam,
    select: (data) => data.pages.flat(),
    enabled: hasGroupSelected,
    staleTime: 10 * 60 * 1000,
    placeholderData: keepPreviousData,
  });

  const { rootRef: restaurantSubGroupMenuRef, targetRef: restaurantSubGroupLoaderRef } =
    useInfiniteScrollObserver({
      fetchNextPage: fetchNextRestaurantSubGroupsPage,
      hasNextPage: hasNextRestaurantSubGroupsPage,
      isFetchingNextPage: restaurantSubGroupsFetchingNextPage,
    });

  const fetchRestaurants = async ({ pageParam = 1 }) => {
    const response = await api.get("/v1/admin/dropdowns/restaurants_list", {
      params: {
        page: pageParam,
        per_page: PerPage,
        search: debouncedRestaurantSearch || undefined,
        restaurant_group_ids: hasGroupSelected ? filters.restaurant_group_id : undefined,
        restaurant_sub_group_id: hasSubGroupSelected ? filters.restaurant_sub_group_id : undefined,
      },
    });
    return (
      response?.data?.map((record) => ({
        ...normalizeRestaurant(record),
        id: Number(record?.value ?? record?.id),
      })) || []
    );
  };

  const {
    data: restaurants = [],
    isFetching: restaurantFetching,
    isFetchingNextPage: restaurantsFetchingNextPage,
    fetchNextPage: fetchNextRestaurantsPage,
    hasNextPage: hasNextRestaurantsPage,
  } = useInfiniteQuery({
    queryKey: Query_Keys.restaurants_dropdown({
      restaurant_group_id: filters.restaurant_group_id,
      restaurant_sub_group_id: filters.restaurant_sub_group_id,
      search: debouncedRestaurantSearch,
      source: "labor_report",
    }),
    queryFn: fetchRestaurants,
    initialPageParam: 1,
    getNextPageParam: getDropdownNextPageParam,
    select: (data) => data.pages.flat(),
    staleTime: 10 * 60 * 1000,
    placeholderData: keepPreviousData,
  });

  const { rootRef: restaurantMenuRef, targetRef: restaurantLoaderRef } =
    useInfiniteScrollObserver({
      fetchNextPage: fetchNextRestaurantsPage,
      hasNextPage: hasNextRestaurantsPage,
      isFetchingNextPage: restaurantsFetchingNextPage,
    });

  const appliedBusinessDate = toIsoDate(appliedFilters.business_date);

  // One reading of the applied filters for the table, the group Total and both downloads, so an
  // export can never cover a scope the table on screen does not.
  const appliedScope = useMemo(() => scopeToParams(appliedFilters), [appliedFilters]);

  const appliedScopeParams = () => ({
    business_date: appliedBusinessDate,
    ...appliedScope,
  });

  const fetchLaborReport = async () => {
    const response = await api.get("/v1/admin/labor_reports", {
      params: {
        page,
        per_page: itemsPerPage,
        ...appliedScopeParams(),
      },
    });

    return {
      ...(response?.data || {}),
      // Total-Count counts restaurants, not rows — pagination is by restaurant, 3 rows each.
      pageCount: Number(response?.headers?.["total-count"] || 0),
    };
  };

  const fetchLaborReportSummary = async () => {
    const response = await api.get("/v1/admin/labor_reports/summary", {
      params: appliedScopeParams(),
    });
    return response?.data || {};
  };

  const {
    data: laborReport,
    isFetching,
    error,
  } = useQuery({
    queryKey: Query_Keys.labor_reports({
      queryFilter: {
        ...appliedFilters,
        business_date: toIsoDate(appliedFilters.business_date),
      },
      page,
      itemsPerPage,
    }),
    queryFn: fetchLaborReport,
    staleTime: 10 * 60 * 1000,
    placeholderData: keepPreviousData,
  });

  const {
    data: laborSummary,
    error: summaryError,
    // True while this scope's Total is still loading and `laborSummary` is the previous scope's.
    isPlaceholderData: summaryIsForPreviousScope,
  } = useQuery({
    queryKey: Query_Keys.labor_report_summary({
      queryFilter: {
        ...appliedFilters,
        business_date: toIsoDate(appliedFilters.business_date),
      },
    }),
    queryFn: fetchLaborReportSummary,
    staleTime: 10 * 60 * 1000,
    placeholderData: keepPreviousData,
  });

  useEffect(() => {
    setRestaurantGroupOptionsCache((current) =>
      mergeOptionsIfChanged(current, restaurantGroups, "label", ["value"]),
    );
  }, [restaurantGroups]);

  useEffect(() => {
    setRestaurantSubGroupOptionsCache((current) =>
      mergeOptionsIfChanged(current, restaurantSubGroups, "value", ["label"]),
    );
  }, [restaurantSubGroups]);

  useEffect(() => {
    setRestaurantOptionsCache((current) =>
      mergeOptionsIfChanged(current, restaurants, "id", ["name"]),
    );
  }, [restaurants]);

  useEffect(() => {
    const failed = error || summaryError;
    if (!failed) return;
    showNotification(failed?.response?.data?.error || "Failed to load labor report.", "error");
  }, [error, summaryError, showNotification]);

  const selectedRestaurantGroupOption = restaurantGroupOptionsCache?.find(
    (group) => `${group?.label}` === `${filters.restaurant_group_id}`,
  );
  const displayedRestaurantGroups = restaurantGroupSearch.trim()
    ? restaurantGroups
    : selectedRestaurantGroupOption
      ? mergeUniqueByKey(restaurantGroups, [selectedRestaurantGroupOption], "label")
      : restaurantGroups;

  const selectedSubGroupOption = restaurantSubGroupOptionsCache?.find(
    (subGroup) => `${subGroup?.value}` === `${filters.restaurant_sub_group_id}`,
  );
  const displayedSubGroups = restaurantSubGroupSearch.trim()
    ? restaurantSubGroups
    : selectedSubGroupOption
      ? mergeUniqueByKey(restaurantSubGroups, [selectedSubGroupOption], "value")
      : restaurantSubGroups;

  const selectedRestaurantOptions = restaurantOptionsCache?.filter((restaurant) =>
    filters.restaurant_ids?.some((id) => `${id}` === `${restaurant?.id}`),
  );
  const displayedRestaurants = restaurantSearch.trim()
    ? restaurants
    : mergeUniqueByKey(restaurants, selectedRestaurantOptions, "id");

  const getRestaurantGroupName = (id) => {
    if (id === ALL_FILTER_VALUE) return "All restaurant groups";
    const cached = restaurantGroupOptionsCache?.find((group) => `${group?.label}` === `${id}`);
    if (cached?.value) return cached.value;
    return restaurantGroups?.find((record) => `${record.label}` === `${id}`)?.value || "All restaurant groups";
  };

  const getSubGroupName = (id) => {
    if (id === ALL_FILTER_VALUE) return "All sub-groups";
    const cached = restaurantSubGroupOptionsCache?.find((sg) => `${sg?.value}` === `${id}`);
    if (cached?.label) return cached.label;
    return restaurantSubGroups?.find((record) => `${record.value}` === `${id}`)?.label || "All sub-groups";
  };

  const getRestaurantDisplayValue = () => {
    if (!filters.restaurant_ids?.length || filters.restaurant_ids.includes(ALL_FILTER_VALUE)) {
      return "All restaurants";
    }
    if (filters.restaurant_ids.length === 1) {
      const restaurant = restaurantOptionsCache?.find(
        (record) => `${record.id}` === `${filters.restaurant_ids[0]}`,
      );
      return restaurant?.name || "All restaurants";
    }
    return `${filters.restaurant_ids.length} Restaurants Selected`;
  };

  const markChanged = () => setFiltersChanged(true);

  const handleRestaurantGroupChange = (event) => {
    setRestaurantSubGroupSearch("");
    setRestaurantSearch("");
    setFilters((prev) => ({
      ...prev,
      restaurant_group_id: event.target.value,
      restaurant_sub_group_id: ALL_FILTER_VALUE,
      restaurant_ids: [ALL_FILTER_VALUE],
    }));
    markChanged();
  };

  const handleRestaurantGroupClear = () => {
    setRestaurantSubGroupSearch("");
    setRestaurantSearch("");
    setFilters((prev) => ({
      ...prev,
      restaurant_group_id: ALL_FILTER_VALUE,
      restaurant_sub_group_id: ALL_FILTER_VALUE,
      restaurant_ids: [ALL_FILTER_VALUE],
    }));
    markChanged();
  };

  const handleSubGroupChange = (event) => {
    setRestaurantSearch("");
    setFilters((prev) => ({
      ...prev,
      restaurant_sub_group_id: event.target.value,
      restaurant_ids: [ALL_FILTER_VALUE],
    }));
    markChanged();
  };

  const handleSubGroupClear = () => {
    setRestaurantSearch("");
    setFilters((prev) => ({
      ...prev,
      restaurant_sub_group_id: ALL_FILTER_VALUE,
      restaurant_ids: [ALL_FILTER_VALUE],
    }));
    markChanged();
  };

  const handleRestaurantChange = (event) => {
    const { value } = event.target;
    if ((value.length === 1 && value.includes(ALL_FILTER_VALUE)) || value.length === 0) {
      setFilters((prev) => ({ ...prev, restaurant_ids: [ALL_FILTER_VALUE] }));
    } else {
      setFilters((prev) => ({
        ...prev,
        restaurant_ids: value.filter((record) => record !== ALL_FILTER_VALUE),
      }));
    }
    markChanged();
  };

  const handleRestaurantClear = () => {
    setFilters((prev) => ({ ...prev, restaurant_ids: [ALL_FILTER_VALUE] }));
    markChanged();
  };

  const handleBusinessDateChange = (value) => {
    setFilters((prev) => ({ ...prev, business_date: normalizeDayjsValue(value) }));
    markChanged();
  };

  const handleApplyFilters = () => {
    // Caught here rather than sent as a blank param, which the endpoint would read as "today".
    if (!filters.business_date) {
      showNotification(INVALID_DATE_MESSAGE, "error");
      return;
    }
    setAppliedFilters(filters);
    setFiltersChanged(false);
    setPage(1);
  };

  const handleReset = () => {
    const resetFilters = defaultLaborFilters();
    setFilters(resetFilters);
    setAppliedFilters(resetFilters);
    setFiltersChanged(false);
    setPage(1);
    setItemsPerPage(25);
    setRestaurantGroupSearch("");
    setRestaurantSubGroupSearch("");
    setRestaurantSearch("");
  };

  const handleOpenExport = useCallback(() => setExportOpen(true), []);
  const handleCloseExport = useCallback(() => setExportOpen(false), []);

  // Both formats cover the WHOLE filtered scope rather than the page on screen, so the exported
  // Total is the sum of the exported rows. The dialog stays open on failure — the scope cap (422)
  // and the busy-renderer back-off (429) are both worth retrying from.
  const handleExport = useCallback(
    async (format) => {
      const downloaded = await runExport({
        format,
        url: buildGroupExportUrl({
          format,
          businessDate: appliedBusinessDate,
          scope: appliedScope,
        }),
        fallbackFilename: exportFilename({ format, businessDate: appliedBusinessDate }),
      });

      if (downloaded) setExportOpen(false);
    },
    [runExport, appliedBusinessDate, appliedScope],
  );

  const responseColumns = laborReport?.columns;

  const columns = useMemo(() => {
    const measureColumns = PERIODS.flatMap((period) =>
      MEASURES.map((measure) => ({
        key: `${period}_${measure.field}`,
        // Date headers are generated server-side so they can never disagree with the numbers.
        label: measure.label ?? responseColumns?.[period]?.[measure.labelFrom] ?? "",
        sortable: false,
        align: "center",
        width: measure.label ? 110 : 150,
        headerSx: columnHeaderStyles,
      })),
    );

    return [
      {
        key: "business",
        label: "Business",
        sortable: false,
        width: 200,
        headerSx: columnHeaderStyles,
      },
      ...measureColumns,
    ];
  }, [responseColumns]);

  const headerRows = useMemo(
    () => [
      [
        { key: "business-spacer", label: "", sx: groupSpacerStyles },
        { key: "daily", label: "Daily", colSpan: MEASURES.length, sx: groupBandStyles },
        { key: "mtd", label: "MTD", colSpan: MEASURES.length, sx: groupBandStyles },
      ],
    ],
    [],
  );

  // Each restaurant flattens into 3 sibling rows; `bucket` drives the row styling.
  const rows = useMemo(
    () =>
      (laborReport?.restaurants || []).flatMap((restaurant) =>
        (restaurant.rows || []).map((row) => ({
          id: `${restaurant.restaurant_id}-${row.bucket}`,
          bucket: row.bucket,
          business: row.label,
          ...measureCells(row),
        })),
      ),
    [laborReport?.restaurants],
  );

  // The group Total renders as a table footer, not a data row, so it stays out of the row count,
  // out of sorting and off the paginated row set.
  //
  // Withheld whenever it might not describe the rows on screen: while this scope's Total is still
  // loading (`keepPreviousData` would otherwise show the previous scope's figure under the new
  // scope's rows), and while the two responses report different business dates. Both mean a
  // request is in flight, so holding the footer back beats captioning rows with the wrong Total.
  const footerRows = useMemo(() => {
    const total = laborSummary?.total;
    if (!total) return [];
    if (summaryIsForPreviousScope) return [];
    if (laborSummary?.business_date !== laborReport?.business_date) return [];

    return [
      {
        key: "group-total",
        sx: groupTotalRowStyles,
        business: (
          <Box component="span" sx={groupTotalLabelStyles}>
            {total.label}
            <LightTooltip title={GROUP_TOTAL_TOOLTIP} placement="top">
              <StyledInfoIcon data-testid="labor-group-total-info" />
            </LightTooltip>
          </Box>
        ),
        ...measureCells(total),
      },
    ];
  }, [laborSummary, laborReport?.business_date, summaryIsForPreviousScope]);

  const getRowProps = (row) => ({
    "data-bucket": row.bucket,
    sx: row.bucket === "total" ? totalRowStyles : detailRowStyles,
  });

  return (
    <Box sx={{ height: "100%", width: "100%", overflowY: "scroll", overflowX: "hidden" }}>
      <StyledBreadCrumbContainer>
        <Breadcrumbs>
          <NavigableBreadcrumbLink LinkComponent={StyledLink} label="Home" to="/admin/dashboard" />
          <StyledLink color="#515152">Reports</StyledLink>
          <StyledLink color="#515152">Sales</StyledLink>
          <StyledLink color="#515152">Labor Report</StyledLink>
        </Breadcrumbs>
      </StyledBreadCrumbContainer>

      <Box sx={filterContainerStyles}>
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", lg: "row" },
            justifyContent: "space-between",
            alignItems: { xs: "flex-start", lg: "center" },
            gap: { xs: 2, lg: 1 },
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              gap: { xs: 2, sm: "12px" },
              flexWrap: "wrap",
              alignItems: { xs: "flex-start", sm: "center" },
              flex: 1,
            }}
          >
            <FormControl size="small" sx={{ minWidth: { xs: "100%", sm: "185px" } }}>
              <Select
                data-testid="labor-restaurant-group"
                value={filters.restaurant_group_id}
                renderValue={() => getRestaurantGroupName(filters.restaurant_group_id)}
                onChange={handleRestaurantGroupChange}
                onClose={() => setTimeout(() => setRestaurantGroupSearch(""), 250)}
                sx={selectBaseStyles}
                endAdornment={
                  hasGroupSelected ? (
                    <InputAdornment position="end" sx={{ position: "absolute", right: 25 }}>
                      <IconButton edge="end" size="small" disableRipple onClick={handleRestaurantGroupClear}>
                        <ClearIcon sx={{ fontSize: "20px" }} />
                      </IconButton>
                    </InputAdornment>
                  ) : null
                }
                MenuProps={dropdownMenuProps(restaurantGroupMenuRef)}
              >
                <DropdownSearchBar
                  changeSearchText={setRestaurantGroupSearch}
                  placeholder="Search restaurant group"
                  isFetching={restaurantGroupFetching}
                />
                {displayedRestaurantGroups.map((group) => (
                  <StyledMenuItem key={group.label} value={group.label}>
                    {filters.restaurant_group_id === group.label ? (
                      <SelectedMenuItem data={group.value} />
                    ) : (
                      group.value
                    )}
                  </StyledMenuItem>
                ))}
                <DropdownInfiniteLoader
                  hasNextPage={hasNextRestaurantGroupsPage}
                  targetRef={restaurantGroupLoaderRef}
                />
              </Select>
            </FormControl>

            <FormControl
              size="small"
              sx={{ minWidth: { xs: "100%", sm: "175px" } }}
              disabled={!hasGroupSelected}
            >
              <Select
                data-testid="labor-restaurant-sub-group"
                value={filters.restaurant_sub_group_id}
                renderValue={() => getSubGroupName(filters.restaurant_sub_group_id)}
                onChange={handleSubGroupChange}
                onClose={() => setTimeout(() => setRestaurantSubGroupSearch(""), 250)}
                sx={selectBaseStyles}
                endAdornment={
                  hasSubGroupSelected ? (
                    <InputAdornment position="end" sx={{ position: "absolute", right: 25 }}>
                      <IconButton edge="end" size="small" disableRipple onClick={handleSubGroupClear}>
                        <ClearIcon sx={{ fontSize: "20px" }} />
                      </IconButton>
                    </InputAdornment>
                  ) : null
                }
                MenuProps={dropdownMenuProps(restaurantSubGroupMenuRef)}
              >
                <DropdownSearchBar
                  changeSearchText={setRestaurantSubGroupSearch}
                  placeholder="Search sub-group"
                  isFetching={restaurantSubGroupFetching}
                />
                {displayedSubGroups.map((subGroup) => (
                  <StyledMenuItem key={subGroup.value} value={subGroup.value}>
                    {`${filters.restaurant_sub_group_id}` === `${subGroup.value}` ? (
                      <SelectedMenuItem data={subGroup.label} />
                    ) : (
                      subGroup.label
                    )}
                  </StyledMenuItem>
                ))}
                <DropdownInfiniteLoader
                  hasNextPage={hasNextRestaurantSubGroupsPage}
                  targetRef={restaurantSubGroupLoaderRef}
                />
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: { xs: "100%", sm: "195px" } }}>
              <Select
                data-testid="labor-restaurant"
                multiple
                value={filters.restaurant_ids}
                renderValue={() => getRestaurantDisplayValue()}
                onChange={handleRestaurantChange}
                onClose={() => setTimeout(() => setRestaurantSearch(""), 250)}
                sx={selectBaseStyles}
                endAdornment={
                  !filters.restaurant_ids.includes(ALL_FILTER_VALUE) ? (
                    <InputAdornment position="end" sx={{ position: "absolute", right: 25 }}>
                      <IconButton edge="end" size="small" disableRipple onClick={handleRestaurantClear}>
                        <ClearIcon sx={{ fontSize: "20px" }} />
                      </IconButton>
                    </InputAdornment>
                  ) : null
                }
                MenuProps={dropdownMenuProps(restaurantMenuRef)}
              >
                <DropdownSearchBar
                  changeSearchText={setRestaurantSearch}
                  placeholder="Search restaurant"
                  isFetching={restaurantFetching}
                />
                {displayedRestaurants.map((restaurant) => (
                  <StyledMenuItem key={restaurant.id} value={restaurant.id}>
                    {filters.restaurant_ids.includes(restaurant.id) ? (
                      <SelectedMenuItem data={restaurant.name} />
                    ) : (
                      restaurant.name
                    )}
                  </StyledMenuItem>
                ))}
                <DropdownInfiniteLoader
                  hasNextPage={hasNextRestaurantsPage}
                  targetRef={restaurantLoaderRef}
                />
              </Select>
            </FormControl>

            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                format="MM/DD/YYYY"
                value={normalizeDayjsValue(filters.business_date)}
                onChange={handleBusinessDateChange}
                slots={{ openPickerIcon: CalendarMonthIcon }}
                slotProps={{
                  textField: {
                    size: "small",
                    sx: { ...datePickerStyles, minWidth: 160 },
                    inputProps: { "data-testid": "labor-business-date" },
                  },
                }}
              />
            </LocalizationProvider>
          </Box>

          <Box
            sx={{
              display: "flex",
              gap: 1,
              alignItems: "center",
              flexWrap: "wrap",
              width: { xs: "100%", lg: "auto" },
            }}
          >
            <ResetButton onClick={handleReset} />
            <UpdateButton
              onClick={handleApplyFilters}
              filtersChanged={filtersChanged}
              status={isFetching}
            />
          </Box>
        </Box>
      </Box>

      <MenuHeading report={false}>Labor Report</MenuHeading>

      <Box sx={{ px: 2, pb: 2 }}>
        {isFetching ? (
          <Box data-testid="labor-table-loader">
            <Loader />
          </Box>
        ) : (
          <IndexTable
            report={false}
            columns={columns}
            headerRows={headerRows}
            data={rows}
            type="restaurants"
            tableId="labor-report"
            itemsPerPage={itemsPerPage}
            setItemsPerPage={setItemsPerPage}
            page={page}
            setPage={setPage}
            pageCount={laborReport?.pageCount || 0}
            getRowProps={getRowProps}
            footerRows={footerRows}
            disableColumnResize
            emptyStateText="No labor data for the selected filters"
            // csvLoading is left off deliberately: it swaps the download control for an
            // unlabelled spinner mid-export and drops keyboard focus. The picker's own
            // Download button already carries the progress state.
            showDownloadButton
            downloadButtonLabel="Export labor report"
            disableDownloadButton={!laborReport?.restaurants?.length || exporting}
            handleExportCsv={handleOpenExport}
          />
        )}
      </Box>

      <LaborReportExportDialog
        open={exportOpen}
        onClose={handleCloseExport}
        onConfirm={handleExport}
        loading={exporting}
        description={`Select the format you want to download. This report covers every restaurant in the current filters${
          responseColumns?.daily?.current ? ` for ${responseColumns.daily.current}` : ""
        }.`}
      />
    </Box>
  );
};
